import { useNavigate } from 'react-router-dom';
import { 
    CalendarDaysIcon, 
    BeakerIcon,
    PlusCircleIcon,
    RectangleGroupIcon
} from '@heroicons/react/24/outline';

/**
 * Componente QuickActions - Accesos rápidos del Dashboard
 * Botones de acceso directo a las funcionalidades más utilizadas
 * 
 * @component
 * @example
 * return (
 *   <QuickActions />
 * )
 */
const QuickActions = () => {
    const navigate = useNavigate();

    const actions = [
        {
            id: 1,
            label: 'Agenda',
            description: 'Ver y gestionar citas',
            icon: CalendarDaysIcon,
            route: '/admin/agenda',
            gradient: 'from-[#0033ff] to-[#0066ff]',
            iconBg: 'bg-gradient-to-br from-[#0033ff] to-[#0066ff]',
            hoverShadow: 'hover:shadow-[0_0_20px_rgba(0,51,255,0.4)]'
        },
        {
            id: 2,
            label: 'Servicios',
            description: 'Catálogo de servicios',
            icon: BeakerIcon,
            route: '/admin/servicios',
            gradient: 'from-[#0066ff] to-[#11cdef]',
            iconBg: 'bg-gradient-to-br from-[#0066ff] to-[#11cdef]',
            hoverShadow: 'hover:shadow-[0_0_20px_rgba(0,102,255,0.4)]'
        },
        {
            id: 3,
            label: 'Nuevo Servicio',
            description: 'Crear servicio',
            icon: PlusCircleIcon,
            route: '/admin/servicios/new',
            gradient: 'from-[#11cdef] to-[#0033ff]',
            iconBg: 'bg-gradient-to-br from-[#11cdef] to-[#0033ff]',
            hoverShadow: 'hover:shadow-[0_0_20px_rgba(17,205,239,0.4)]'
        },
        {
            id: 4,
            label: 'Categorías',
            description: 'Gestionar categorías',
            icon: RectangleGroupIcon,
            route: '/admin/categorias',
            gradient: 'from-[#0033ff] to-[#11cdef]',
            iconBg: 'bg-gradient-to-br from-[#0033ff] to-[#11cdef]',
            hoverShadow: 'hover:shadow-[0_0_20px_rgba(0,51,255,0.3)]'
        }
    ];

    const handleNavigation = (route) => {
        navigate(route);
    };

    return (
        <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800">
                    Accesos Rápidos
                </h3>
                <div className="h-1 flex-1 ml-4 bg-gradient-to-r from-[#0033ff] via-[#0066ff] to-[#11cdef] rounded-full"></div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {actions.map((action) => {
                    const IconComponent = action.icon;
                    return (
                        <button
                            key={action.id}
                            onClick={() => handleNavigation(action.route)}
                            className={`
                                group relative bg-white rounded-xl p-6 border-2 border-gray-200
                                transition-all duration-300 cursor-pointer
                                hover:border-transparent hover:-translate-y-1
                                ${action.hoverShadow}
                            `}
                        >
                            {/* Fondo gradiente al hover */}
                            <div className={`
                                absolute inset-0 rounded-xl opacity-0 group-hover:opacity-5
                                bg-gradient-to-br ${action.gradient}
                                transition-opacity duration-300
                            `}></div>

                            {/* Contenido */}
                            <div className="relative z-10">
                                {/* Icono */}
                                <div className={`
                                    ${action.iconBg} 
                                    w-14 h-14 rounded-lg flex items-center justify-center mb-4
                                    group-hover:scale-110 transition-transform duration-300
                                    shadow-lg
                                `}>
                                    <IconComponent className="h-7 w-7 text-white" />
                                </div>

                                {/* Texto */}
                                <h3 className="text-lg font-bold text-gray-800 mb-1">
                                    {action.label}
                                </h3>
                                <p className="text-xs text-gray-500">
                                    {action.description}
                                </p>
                            </div>

                            {/* Indicador de acción */}
                            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <svg 
                                    className="w-5 h-5 text-gray-400" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M13 7l5 5m0 0l-5 5m5-5H6" 
                                    />
                                </svg>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default QuickActions;
